import { useSuspenseQuery } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import { Suspense } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  SearchableMultiSelect,
} from "~/components/ui";
import {
  GetReferenceForFormDocument,
  type GetReferenceForFormQuery,
  type GetReferenceForFormQueryVariables,
  GetReferencePublishersDocument,
} from "~/features/references/gqls";
import { useReferenceUpsert } from "~/features/references/hooks";
import { type ReferenceFormData, zReferenceFormData } from "~/features/references/types";
import { useTenant } from "~/features/tenant/context";

const FORM_ID = "reference-form";

const EMPTY: ReferenceFormData = {
  title: "",
  shortCode: "",
  isbn: "",
  documentUrl: "",
  authors: [],
  publisherIds: [],
  newPublishers: [],
  edition: "",
  publishedDate: "",
};

interface ReferenceFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** When set, edit that reference; otherwise create. */
  referenceId?: string | null;
}

export function ReferenceFormDialog({ open, onOpenChange, referenceId }: ReferenceFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(referenceId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="capitalize">
            {`${isEdit ? t("ui.edit") : t("ui.add")} ${t("model.reference.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          {isEdit && referenceId ? (
            <EditBody referenceId={referenceId} onClose={() => onOpenChange(false)} />
          ) : (
            <FormBody defaults={EMPTY} onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function EditBody({ referenceId, onClose }: { referenceId: string; onClose: () => void }) {
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const { data } = useSuspenseQuery<GetReferenceForFormQuery, GetReferenceForFormQueryVariables>(
    GetReferenceForFormDocument,
    { variables: { tenantId, id: referenceId } },
  );
  const ref = data.references[0];
  if (!ref) return null;
  const defaults: ReferenceFormData = {
    id: ref.id,
    title: ref.title,
    shortCode: ref.shortCode,
    isbn: ref.isbn ?? "",
    documentUrl: ref.documentUrl ?? "",
    edition: ref.edition ?? "",
    publishedDate: ref.publishedDate ?? "",
    publisherIds: ref.referencePublishers.map(({ publisherId }) => publisherId),
    newPublishers: [],
    authors: ((ref.authors ?? []) as string[]).map((name) => ({ name })),
  };
  return (
    <FormBody
      defaults={defaults}
      existingPublishers={ref.referencePublishers.map(({ publisherId, publisher }) => ({
        id: publisherId,
        name: publisher?.name ?? publisherId,
      }))}
      onClose={onClose}
    />
  );
}

function FormBody({
  defaults,
  existingPublishers = [],
  onClose,
}: {
  defaults: ReferenceFormData;
  existingPublishers?: { id: string; name: string }[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const form = useForm<ReferenceFormData>({
    resolver: zodResolver(zReferenceFormData, {
      error: (issue) =>
        issue.path?.[0] === "publishedDate" ? t("feature.references.form.invalidPublishedDate") : undefined,
    }),
    defaultValues: defaults,
  });
  const { tenantId } = useTenant();
  if (!tenantId) throw new Error("Tenant ID is required");
  const { data } = useSuspenseQuery(GetReferencePublishersDocument, { variables: { tenantId } });
  const selectedPublisherIds = useWatch({ control: form.control, name: "publisherIds" });
  const activePublisherIds = data.publishers.map(({ id }) => id);
  const publishers = Array.from(
    new Map(
      [...data.publishers, ...existingPublishers.filter(({ id }) => selectedPublisherIds.includes(id))].map(
        (publisher) => [publisher.id, publisher],
      ),
    ).values(),
  );
  const nameCounts = new Map<string, number>();
  for (const { name } of publishers) nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1);
  const publisherOptions = publishers.map(({ id, name }) => ({
    id,
    label: activePublisherIds.includes(id)
      ? (nameCounts.get(name) ?? 0) > 1
        ? `${name} (${id})`
        : name
      : `${name} (${id}) — ${t("feature.references.form.inactivePublisher")}`,
  }));
  const newPublishers = useFieldArray({ control: form.control, name: "newPublishers" });
  const authors = useFieldArray({ control: form.control, name: "authors" });
  const { submit, processing } = useReferenceUpsert({
    onSuccess: onClose,
    initialPublisherIds: defaults.publisherIds,
    activePublisherIds,
  });

  return (
    <Form {...form}>
      <form id={FORM_ID} onSubmit={form.handleSubmit(submit)} className="space-y-4">
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.reference.title")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="shortCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.reference.shortCode")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="isbn"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.reference.isbn")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="publisherIds"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.reference.publishers")}</FormLabel>
              <FormControl>
                <SearchableMultiSelect
                  options={publisherOptions}
                  selectedIds={field.value}
                  onChange={field.onChange}
                  placeholder={t("feature.references.form.selectPublishers")}
                  searchPlaceholder={t("feature.references.form.selectPublishers")}
                  emptyText={t("ui.notFound")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {newPublishers.fields.map((publisher, index) => (
          <div key={publisher.id} className="flex items-start gap-2">
            <FormField
              control={form.control}
              name={`newPublishers.${index}.name`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel>{t("feature.references.form.newPublisher")}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => newPublishers.remove(index)}
              aria-label={t("ui.delete")}
            >
              <Trash2 />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={() => newPublishers.append({ name: "" })}>
          <Plus />
          <span>{t("feature.references.form.newPublisher")}</span>
        </Button>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="publishedDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.reference.publishedDate")}</FormLabel>
                <FormControl>
                  <Input {...field} placeholder={t("feature.references.form.publishedDatePlaceholder")} />
                </FormControl>
                <FormDescription>{t("feature.references.form.publishedDateHint")}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="edition"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("model.reference.edition")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="documentUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataImportProject.document")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <Label className="capitalize">{t("model.reference.authors")}</Label>
          {authors.fields.length === 0 ? (
            <p className="text-12-regular text-fg-3">{t("feature.references.form.noAuthors")}</p>
          ) : (
            <div className="space-y-2">
              {authors.fields.map((f, index) => (
                <div key={f.id} className="flex items-start gap-2">
                  <FormField
                    control={form.control}
                    name={`authors.${index}.name`}
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormControl>
                          <Input {...field} placeholder={t("model.common.name")} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => authors.remove(index)}
                    aria-label={t("ui.delete")}
                  >
                    <Trash2 />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => authors.append({ name: "" })}>
            <Plus />
            <span>{t("feature.references.form.addAuthor")}</span>
          </Button>
        </div>
      </form>
      <DialogFooter className="pt-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={processing}>
          {t("ui.cancel")}
        </Button>
        <Button type="submit" form={FORM_ID} disabled={processing}>
          {t("ui.save")}
        </Button>
      </DialogFooter>
    </Form>
  );
}
